import { Search, X } from "lucide-react";

import { inputChassis } from "@/components/ui/input";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./value-picker-trigger.i18n";

/**
 * Design 14f — the trigger that opens a value picker. It borrows the field
 * chassis so it lines up with the Inputs and Selects around it; the search icon
 * says "this opens a search", and swaps for a clear × once there is something
 * to clear.
 *
 * The adornments are siblings of the button rather than children, so the
 * chassis's own `disabled:opacity-50` cannot reach them — they dim on the
 * `disabled` prop instead.
 */
export function ValuePickerTrigger({
  value,
  placeholder,
  onOpen,
  onClear,
  disabled,
  id,
  className,
}: {
  value?: string | null;
  placeholder: string;
  onOpen: () => void;
  onClear?: () => void;
  disabled?: boolean;
  id?: string;
  className?: string;
}) {
  const t = useT(T);
  const filled = Boolean(value);

  return (
    <div className={cn("relative flex w-full items-center", className)}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={onOpen}
        className={cn(
          inputChassis,
          "items-center pr-9 text-left text-sm",
          !filled && "text-muted-foreground",
        )}
      >
        <span className="line-clamp-1 min-w-0">{filled ? value : placeholder}</span>
      </button>
      {filled && onClear ? (
        <button
          type="button"
          disabled={disabled}
          onClick={onClear}
          aria-label={t.clear}
          className="absolute right-2.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      ) : (
        <Search
          className={cn(
            "pointer-events-none absolute right-2.5 h-4 w-4 text-muted-foreground",
            disabled && "opacity-50",
          )}
        />
      )}
    </div>
  );
}
